import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, Heart, Moon, Activity, Smile } from 'lucide-react';
import { api } from './api';
import { MaternalBackLink, MaternalDisclaimer, MaternalLoader, MaternalEmptyState } from './_ui';

/**
 * Maternal · Trends & history
 *
 * Calm 7-day picture: score trend, mood/energy/sleep trend pills, total
 * symptoms logged, and a small bar chart of daily scores.
 *
 * No graph library — pure SVG / CSS, lightweight + fast.
 */

const TREND_LABELS = {
  improving: { label: 'gently lifting', icon: TrendingUp,   tone: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  declining: { label: 'softening',      icon: TrendingDown, tone: 'text-amber-700 bg-amber-50 border-amber-200' },
  steady:    { label: 'steady',         icon: Minus,        tone: 'text-sky-700 bg-sky-50 border-sky-200' },
};

const TrendPill = ({ trend, label, icon: Icon }) => {
  const t = TREND_LABELS[trend] || TREND_LABELS.steady;
  const TIc = t.icon;
  return (
    <div className={`rounded-xl border px-3 py-2 ${t.tone}`} data-testid={`maternal-trends-pill-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-4 w-4 shrink-0" />
          <span className="text-sm font-bold truncate">{label}</span>
        </div>
        <span className="text-[11px] font-semibold inline-flex items-center gap-1 shrink-0">
          <TIc className="h-3 w-3" />
          {t.label}
        </span>
      </div>
    </div>
  );
};

const MaternalTrends = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .trends()
      .then((res) => setData(res?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const series = useMemo(() => data?.score_series || [], [data]);
  const max = useMemo(() => (series.length ? Math.max(100, ...series) : 100), [series]);
  const avg = useMemo(() => (series.length ? Math.round(series.reduce((a,b) => a + b, 0) / series.length) : 0), [series]);

  if (loading) {
    return <MaternalLoader label="Reading your last 7 days…" testid="maternal-trends-loading" />;
  }

  if (!data || !data.have_trend) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-trends-empty">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          <MaternalBackLink />
          <MaternalEmptyState
            icon={TrendingUp}
            title="Trends start with a few check-ins"
            body="Log a daily check-in for a few days and a calm 7-day picture will appear here — your mood, energy, and sleep, gently tracked."
            ctaLabel="Log a check-in"
            onCta={() => navigate('/maternal-health/checkin')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-trends">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <MaternalBackLink />

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shrink-0">
              <TrendingUp className="h-5 w-5 text-white" />
            </span>
            <div>
              <p className="text-[11px] font-semibold text-rose-600 uppercase tracking-widest">Trends</p>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">Your last {data.n} day{data.n === 1 ? '' : 's'}</h1>
            </div>
          </div>
        </header>

        {/* Score chart */}
        <article className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4" data-testid="maternal-trends-chart">
          <div className="flex items-end justify-between gap-2 mb-3">
            <div>
              <p className="text-[11px] font-semibold text-rose-700 uppercase tracking-widest">Daily wellbeing score</p>
              <p className="text-3xl font-bold text-gray-900 leading-none mt-1">{avg}<span className="text-sm font-medium text-gray-500">/100 avg</span></p>
            </div>
            <div className="text-right">
              <span className={[
                'inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border',
                (TREND_LABELS[data.score_trend] || TREND_LABELS.steady).tone,
              ].join(' ')}>
                {data.score_trend === 'improving' ? <TrendingUp className="h-3 w-3" /> : data.score_trend === 'declining' ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                {(TREND_LABELS[data.score_trend] || TREND_LABELS.steady).label}
              </span>
            </div>
          </div>

          {/* SVG bar chart — scale to viewport */}
          <div className="h-32 flex items-end gap-1.5" aria-label="7-day score chart" data-testid="maternal-trends-bars">
            {series.map((v, i) => {
              const h = Math.max(6, Math.round((v / max) * 100));
              const tone = v >= 75 ? 'bg-emerald-500' : v >= 50 ? 'bg-amber-400' : 'bg-rose-400';
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                  <span className="text-[10px] text-gray-500 mb-0.5 tabular-nums">{v}</span>
                  <div
                    className={`w-full rounded-md ${tone}`}
                    style={{ height: `${h}%` }}
                    aria-label={`Day ${i + 1}: ${v}/100`}
                  />
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-gray-400 text-center mt-2">Older → newer</p>
        </article>

        {/* Trend pills */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4" data-testid="maternal-trends-pills">
          <TrendPill trend={data.mood_trend}   label="Mood"   icon={Smile} />
          <TrendPill trend={data.energy_trend} label="Energy" icon={Activity} />
          <TrendPill trend={data.sleep_trend}  label="Sleep"  icon={Moon} />
        </section>

        {/* Symptom total */}
        <article className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4 mb-4" data-testid="maternal-trends-symptoms">
          <div className="flex items-center gap-3">
            <Heart className="h-5 w-5 text-rose-500 shrink-0" />
            <div>
              <p className="text-sm text-gray-800 leading-relaxed">
                <span className="font-bold">{data.symptoms_total}</span> symptom{data.symptoms_total === 1 ? '' : 's'} logged across the last {data.n} day{data.n === 1 ? '' : 's'}.
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Patterns matter more than any one day.</p>
            </div>
          </div>
        </article>

        {/* Calm interpretation */}
        <article className="rounded-2xl bg-rose-50 border border-rose-200 p-4 mb-5" data-testid="maternal-trends-note">
          <p className="text-[11px] font-semibold text-rose-800 uppercase tracking-widest mb-1">A gentle note</p>
          <p className="text-sm text-rose-900 leading-relaxed">
            {data.score_trend === 'improving' && 'Things are gently lifting this week — keep the small habits that are working.'}
            {data.score_trend === 'declining' && 'A softer week. Pick one tiny anchor — hydration, sleep, or a short walk — and lean on it tomorrow.'}
            {data.score_trend === 'steady'    && 'Steady week. Steadiness IS the point — your rhythm is doing its job.'}
          </p>
        </article>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate('/maternal-health/checkin')}
            className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-all"
          >
            Log today's check-in
          </button>
          <button
            onClick={() => navigate('/maternal-health/symptoms')}
            className="inline-flex items-center gap-1.5 bg-white border-2 border-gray-200 hover:border-rose-300 text-gray-800 text-sm font-semibold rounded-xl px-4 py-2.5 transition-all"
          >
            See symptoms history
          </button>
        </div>

        <MaternalDisclaimer />
      </div>
    </div>
  );
};

export default MaternalTrends;
